// @givmo/board — the Governance page's Terms part (CBG-YR3-TERMS, frame t2; the backend's contract
// `board-terms.md` §4, §5.3, §6.1).
//
// The board's own term rules, recorded from its governing documents with where each is written —
// Meeting rules' discipline exactly (plan §3.2 Band 2: entered, never derived): the directors' term
// length, the limit on consecutive terms, the officers' term length. Each is the organization's
// number, echoed in the backend's words with its citation and its earlier versions; with nothing
// recorded, Board works nothing out and says so quietly ("Not currently recorded", Meeting rules' own
// badge). Recording one is `board_governance_parameter:manage`, through the same route as every rule.
//
// Then the board's classes: labels only, cited by nothing, because no rule reads them. The Owner
// (`board_member:manage`, the roster's own permission) adds, renames, moves and retires them from
// the Officers part's row menu; a retired class is never deleted and stays on every term naming it.
// With none recorded, the card says so and asks for none.
//
// Every new word is in `terms-words.ts`, approved in copy round CBG-YR3-COPY1; "Add a rule", "Recorded …", "From your
// governing documents: …", "Not currently recorded", the history lines and the intro are Meeting
// rules' own.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isGivmoError, type BoardDirectorClassOut } from '@givmo/api-client';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Dialog,
  ErrorMessage,
  RowActions,
  Select,
  Skeleton,
  TextField,
  Textarea,
  toast,
} from '@givmo/design-system';
import { hasBoardPermission, useBoardHost } from '../../board-host';
import { useBoardOperations } from '../../board-runtime';
import { FieldNote } from '../../components/field-note';
import { displayDate } from '../../lib/display';
import { extractParameters } from '../parameters/parameters-decode';
import { officerRolesL2Words as l2 } from '../roster/officer-roles-l2-words';
import {
  TERM_RULE_BASIS,
  extractDirectorClasses,
  termRule,
  type TermRule,
  type TermRuleKind,
} from '../roster/terms-decode';
import { termsWords as tw } from '../roster/terms-words';
import { GovernancePartHeader } from './governance-part-header';

const QUIET = 'font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary';
const SECONDARY = 'font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary';
const ROW =
  'flex flex-wrap items-center justify-between gap-12 border-t border-border-subtle py-10 first:border-t-0 first:pt-0 last:pb-0';

const RULES: readonly { kind: TermRuleKind; title: string; testId: string }[] = [
  { kind: 'director_term_length', title: tw.directorsTerm, testId: 'terms-card-director-term' },
  { kind: 'director_term_limit', title: tw.consecutiveTerms, testId: 'terms-card-limit' },
  { kind: 'officer_term_length', title: tw.officersTerm, testId: 'terms-card-officer-term' },
];

type ClassEditing =
  | { kind: 'add' }
  | { kind: 'rename'; row: BoardDirectorClassOut }
  | { kind: 'retire'; row: BoardDirectorClassOut };

export function TermsPart() {
  const operations = useBoardOperations();
  const queryClient = useQueryClient();
  const { permissions, orgRef, isReady } = useBoardHost();
  const canRecord = hasBoardPermission(permissions, 'board_governance_parameter:manage');
  const canManageClasses = hasBoardPermission(permissions, 'board_member:manage');
  const org = orgRef ?? '';
  const enabled = isReady && orgRef !== null;
  const [recording, setRecording] = useState(false);
  const [editingClass, setEditingClass] = useState<ClassEditing | null>(null);

  // With their earlier versions, as Meeting rules reads them: each card's history is the rules the
  // board recorded before.
  const parametersOptions = operations.officerListBoardGovernanceParametersOptions({
    path: { org_ref: org },
    query: { include_superseded: true },
  });
  const parametersQuery = useQuery({ ...parametersOptions, enabled, select: extractParameters });
  const classesOptions = operations.officerListBoardDirectorClassesOptions({
    path: { org_ref: org },
  });
  const classesQuery = useQuery({ ...classesOptions, enabled, select: extractDirectorClasses });
  const parameters = parametersQuery.data ?? [];
  const classes = classesQuery.data ?? [];
  const active = classes.filter((row) => row.status !== 'retired');
  const retired = classes.filter((row) => row.status === 'retired');

  const refreshRules = () =>
    queryClient.invalidateQueries({ queryKey: parametersOptions.queryKey });
  const refreshClasses = () => queryClient.invalidateQueries({ queryKey: classesOptions.queryKey });

  const move = useMutation({
    mutationFn: async ({ row, position }: { row: BoardDirectorClassOut; position: number }) => {
      await operations.officerUpdateBoardDirectorClass({
        path: { org_ref: org, class_id: row.id },
        body: { position },
        throwOnError: true,
      });
    },
    onSuccess: () => {
      toast.success(tw.classMoved);
      void refreshClasses();
    },
    onError: (caught) => toast.error(isGivmoError(caught) ? caught.message : tw.classFailed),
  });

  return (
    <div className="flex flex-col gap-24" data-testid="governance-terms">
      <GovernancePartHeader
        description={tw.termsLead}
        actions={
          canRecord ? (
            <Button
              appearance="saas"
              tone="primary"
              disabled={!isReady}
              onClick={() => setRecording(true)}
              testId="terms-record"
            >
              Add a rule
            </Button>
          ) : undefined
        }
      />
      <p className={QUIET}>
        {canRecord
          ? 'Givmo doesn’t read your governing documents. You enter each rule and where it’s written; Givmo does the counting with your numbers and shows where each one came from.'
          : 'Givmo doesn’t read your governing documents.'}
      </p>

      {parametersQuery.isPending && enabled ? (
        <Skeleton className="h-[220px] w-full rounded-lg" testId="terms-rules-loading" />
      ) : (
        RULES.map((rule) => (
          <RuleCard
            key={rule.kind}
            title={rule.title}
            rule={termRule(parameters, rule.kind)}
            testId={rule.testId}
          />
        ))
      )}

      <Card testId="terms-card-classes">
        <CardBody className="flex flex-col gap-12">
          <div className="flex flex-wrap items-center justify-between gap-12">
            <h2 className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
              {tw.classes}
            </h2>
            {canManageClasses ? (
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                disabled={!isReady}
                onClick={() => setEditingClass({ kind: 'add' })}
                testId="terms-classes-add"
              >
                {tw.addClass}
              </Button>
            ) : null}
          </div>
          {classesQuery.isError ? (
            <p className={SECONDARY}>{tw.classesLoadFailed}</p>
          ) : (
            <p className={SECONDARY}>{active.length > 0 ? tw.classesLine : tw.noClasses}</p>
          )}
          {active.length > 0 ? (
            <ul className="flex flex-col">
              {active.map((row, index) => (
                <li key={row.id} className={ROW} data-testid={`terms-class-${row.id}`}>
                  <span className="font-sans text-s14 font-w400 tracking-givmo text-text-strong wrap-anywhere">
                    {row.label}
                  </span>
                  {canManageClasses ? (
                    <RowActions
                      label={l2.actionsFor(row.label)}
                      disabled={move.isPending}
                      testId={`terms-class-actions-${row.id}`}
                      actions={[
                        {
                          label: l2.rename,
                          testId: `terms-class-rename-${row.id}`,
                          onSelect: () => setEditingClass({ kind: 'rename', row }),
                        },
                        ...(index > 0
                          ? [
                              {
                                label: l2.moveUp,
                                testId: `terms-class-up-${row.id}`,
                                onSelect: () => move.mutate({ row, position: index }),
                              },
                            ]
                          : []),
                        ...(index < active.length - 1
                          ? [
                              {
                                label: l2.moveDown,
                                testId: `terms-class-down-${row.id}`,
                                onSelect: () => move.mutate({ row, position: index + 2 }),
                              },
                            ]
                          : []),
                        {
                          label: l2.retire,
                          testId: `terms-class-retire-${row.id}`,
                          onSelect: () => setEditingClass({ kind: 'retire', row }),
                        },
                      ]}
                    />
                  ) : null}
                </li>
              ))}
            </ul>
          ) : null}
          {retired.length > 0 ? (
            <div
              className="flex flex-col gap-8 border-t border-border-subtle pt-12"
              data-testid="terms-classes-retired"
            >
              <span className="font-sans text-s12 font-w600 uppercase tracking-wide text-text-tertiary">
                {l2.retired}
              </span>
              {retired.map((row) => (
                <span key={row.id} className="flex flex-wrap items-baseline gap-x-8">
                  <span className="font-sans text-s14 font-w400 tracking-givmo text-text-secondary wrap-anywhere">
                    {row.label}
                  </span>
                  {row.retired_at ? (
                    <span className={QUIET}>{l2.retiredOn(displayDate(row.retired_at))}</span>
                  ) : null}
                </span>
              ))}
            </div>
          ) : null}
        </CardBody>
      </Card>

      {recording && orgRef !== null ? (
        <TermRuleDialog
          orgRef={orgRef}
          onClose={() => setRecording(false)}
          onRecorded={() => {
            setRecording(false);
            void refreshRules();
          }}
        />
      ) : null}

      {editingClass !== null && orgRef !== null ? (
        <ClassDialog
          orgRef={orgRef}
          editing={editingClass}
          onClose={() => setEditingClass(null)}
          onDone={() => {
            setEditingClass(null);
            void refreshClasses();
          }}
        />
      ) : null}
    </div>
  );
}

/** One rule's card, in Meeting rules' own shape: the number, where it's written, its note and its
 *  earlier versions. */
function RuleCard({
  title,
  rule,
  testId,
}: {
  title: string;
  rule: TermRule | null;
  testId: string;
}) {
  const [showHistory, setShowHistory] = useState(false);
  const earlier = rule?.superseded ?? [];
  return (
    <Card testId={testId}>
      <CardBody className="flex flex-col gap-12">
        <div className="flex flex-wrap items-baseline justify-between gap-12">
          <h2 className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">{title}</h2>
          {rule === null ? (
            <Badge tone="neutral">Not currently recorded</Badge>
          ) : (
            <span className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
              Recorded {displayDate(rule.recordedAt)}
            </span>
          )}
        </div>
        {rule !== null ? (
          <>
            <p
              className="font-sans text-s22 font-w700 tracking-givmo text-text-strong"
              data-testid={`${testId}-value`}
            >
              {rule.summary}
            </p>
            <p className={SECONDARY}>From your governing documents: {rule.citation}</p>
            {rule.note !== null ? <p className={QUIET}>{rule.note}</p> : null}
          </>
        ) : null}
        {earlier.length > 0 ? (
          <div className="flex flex-col gap-8">
            <button
              type="button"
              onClick={() => setShowHistory((open) => !open)}
              className="self-start font-sans text-s13 font-w600 tracking-givmo text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline"
              data-testid={`${testId}-history-toggle`}
            >
              {showHistory
                ? 'Hide earlier versions'
                : `Show ${earlier.length} earlier ${earlier.length === 1 ? 'version' : 'versions'}`}
            </button>
            {showHistory ? (
              <ul className="flex flex-col gap-10 border-l border-border-subtle pl-16">
                {earlier.map((row) => (
                  <li key={row.id} className="flex flex-col gap-2">
                    <span className="font-sans text-s14 font-w600 tracking-givmo text-text-secondary">
                      {row.value_summary}
                    </span>
                    <span className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
                      {row.bylaw_citation} · recorded {displayDate(row.recorded_at)} · replaced{' '}
                      {displayDate(row.superseded_at)}
                    </span>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}

/** "Add a rule" on the Terms tab: one of the three, its count, where it's written, a note. */
function TermRuleDialog({
  orgRef,
  onClose,
  onRecorded,
}: {
  orgRef: string;
  onClose: () => void;
  onRecorded: () => void;
}) {
  const operations = useBoardOperations();
  const [kind, setKind] = useState<TermRuleKind>('director_term_length');
  const [count, setCount] = useState('');
  const [citation, setCitation] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const basis = TERM_RULE_BASIS[kind];

  const record = useMutation({
    mutationFn: async () => {
      await operations.officerRecordBoardGovernanceParameter({
        path: { org_ref: orgRef },
        body: {
          parameter_kind: kind,
          basis,
          count_value: Number.parseInt(count.trim(), 10),
          bylaw_citation: citation.trim(),
          ...(note.trim().length > 0 ? { note: note.trim() } : {}),
        },
        throwOnError: true,
      });
    },
    onSuccess: () => {
      toast.success(tw.ruleRecorded);
      onRecorded();
    },
    onError: (caught) =>
      setError(
        isGivmoError(caught) ? caught.message : 'We couldn’t record that rule. Please try again.',
      ),
  });

  const counted = /^\d+$/.test(count.trim()) ? Number.parseInt(count.trim(), 10) : null;
  const canSave =
    counted !== null &&
    counted >= 1 &&
    counted <= 20 &&
    citation.trim().length > 0 &&
    !record.isPending;

  return (
    <Dialog.Root open onOpenChange={(next) => !next && !record.isPending && onClose()}>
      <Dialog.Content
        aria-label="Add a rule"
        className="flex flex-col gap-16"
        testId="terms-record-dialog"
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          Add a rule
        </Dialog.Title>
        <Select
          label="Which rule"
          appearance="saas"
          value={kind}
          onValueChange={(next) => next !== null && setKind(next as TermRuleKind)}
          options={RULES.map((rule) => ({ value: rule.kind, label: rule.title }))}
          testId="terms-rule-kind"
        />
        <TextField
          label={tw.ruleCount(basis)}
          inputMode="numeric"
          value={count}
          onChange={(event) => setCount(event.target.value)}
          testId="terms-rule-count"
        />
        <TextField
          label="Where this comes from"
          value={citation}
          onChange={(event) => setCitation(event.target.value)}
          testId="terms-rule-citation"
        />
        <FieldNote>
          The section of your own governing documents, e.g. “Art. III §4”. Required — a rule without
          it isn’t attributable to anything.
        </FieldNote>
        <Textarea
          label="Note (optional)"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          rows={2}
          testId="terms-rule-note"
        />
        <p className={QUIET}>
          Recording a new rule replaces the one in force and keeps the old one in your history —
          nothing already recorded changes.
        </p>
        {error !== null ? <ErrorMessage testId="terms-record-error">{error}</ErrorMessage> : null}
        <div className="flex justify-end gap-10">
          <Button
            appearance="saas"
            tone="secondary"
            disabled={record.isPending}
            onClick={onClose}
            testId="terms-record-cancel"
          >
            Cancel
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            loading={record.isPending}
            disabled={!canSave}
            onClick={() => {
              setError(null);
              record.mutate();
            }}
            testId="terms-record-save"
          >
            Record it
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}

/** Add a class, rename one, or confirm a retirement: one dialog, three acts (the Officers part's). */
function ClassDialog({
  orgRef,
  editing,
  onClose,
  onDone,
}: {
  orgRef: string;
  editing: ClassEditing;
  onClose: () => void;
  onDone: () => void;
}) {
  const operations = useBoardOperations();
  const [label, setLabel] = useState(editing.kind === 'rename' ? editing.row.label : '');
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      if (editing.kind === 'add') {
        await operations.officerCreateBoardDirectorClass({
          path: { org_ref: orgRef },
          body: { label: label.trim() },
          throwOnError: true,
        });
      } else if (editing.kind === 'rename') {
        await operations.officerUpdateBoardDirectorClass({
          path: { org_ref: orgRef, class_id: editing.row.id },
          body: { label: label.trim() },
          throwOnError: true,
        });
      } else {
        await operations.officerRetireBoardDirectorClass({
          path: { org_ref: orgRef, class_id: editing.row.id },
          throwOnError: true,
        });
      }
    },
    onSuccess: () => {
      toast.success(
        editing.kind === 'add'
          ? tw.classAdded
          : editing.kind === 'rename'
            ? tw.classRenamed
            : tw.classRetired,
      );
      onDone();
    },
    onError: (caught) => setError(isGivmoError(caught) ? caught.message : tw.classFailed),
  });

  const heading =
    editing.kind === 'add'
      ? tw.addClass
      : editing.kind === 'rename'
        ? tw.renameClass(editing.row.label)
        : tw.retireClass(editing.row.label);
  const canSave =
    !save.isPending &&
    (editing.kind === 'retire' ||
      (label.trim().length > 0 &&
        !(editing.kind === 'rename' && label.trim() === editing.row.label)));

  return (
    <Dialog.Root open onOpenChange={(next) => !next && !save.isPending && onClose()}>
      <Dialog.Content
        aria-label={heading}
        className="flex flex-col gap-16"
        testId={`terms-class-${editing.kind}-dialog`}
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          {heading}
        </Dialog.Title>
        {editing.kind === 'retire' ? (
          <p className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
            {tw.retireClassBody}
          </p>
        ) : (
          <>
            <TextField
              label={tw.className}
              value={label}
              maxLength={40}
              onChange={(event) => setLabel(event.target.value)}
              testId={`terms-class-${editing.kind}-field`}
            />
            <FieldNote>{tw.classNameNote}</FieldNote>
          </>
        )}
        {error !== null ? (
          <ErrorMessage testId={`terms-class-${editing.kind}-error`}>{error}</ErrorMessage>
        ) : null}
        <div className="flex justify-end gap-10">
          <Button
            appearance="saas"
            tone="secondary"
            disabled={save.isPending}
            onClick={onClose}
            testId={`terms-class-${editing.kind}-cancel`}
          >
            Cancel
          </Button>
          <Button
            appearance="saas"
            tone={editing.kind === 'retire' ? 'danger' : 'primary'}
            loading={save.isPending}
            disabled={!canSave}
            onClick={() => {
              setError(null);
              save.mutate();
            }}
            testId={`terms-class-${editing.kind}-save`}
          >
            {editing.kind === 'add'
              ? tw.addClass
              : editing.kind === 'rename'
                ? l2.renameButton
                : l2.retireButton}
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
